import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { QuizsetFilterCriteriaSearchType } from '@/api/gen/_model/quizsetFilterCriteriaSearchType';
import { MagnifyingGlassIcon } from '@/icons';
import { NewMapBanner } from '@/module/featured/components/NewMapBanner';
import { FeaturedQuizRollingBanner } from '@/module/featured/shared/FeaturedQuizzesAside/FeaturedQuizRollingBanner';
import { DrawerFilter } from '@/module/public/components/DrawerFilter';
import { PublicDropdownSchool } from '@/module/public/components/PublicDropdownSchool';
import { SearchFilterBar } from '@/module/public/components/SearchFilterBar';
import { SearchScopeDropdown } from '@/module/public/components/SearchScopeDropdown';
import { TotalLibraryCount } from '@/module/public/components/TotalLibraryCount';
import { usePublicSearchFilterQuery } from '@/module/public/hooks/usePublicSearchFilterQuery';
import { useIsSmallerThanTabletSm } from '@/shared/hooks';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';
import { SearchInputQuizForm } from '@/shared/quizList/SearchField';
import { SearchMetaBarMobile } from '@/shared/quizList/SearchField/SearchMetaBar';
import { SearchOrderDropDownMenu } from '@/shared/quizList/SearchField/SearchOrderDropDownMenu';
import { Button } from '@/ui_v3';
import { formatEnglishTitleCase } from '@/utils/formatEnglishTitleCase';
import { usePublicCategoryFilter } from '../hooks';
export const ContentHeader = () => {
  const {
    state: {
      schoolType
    }
  } = usePublicCategoryFilter();
  const isTabletSm = useIsSmallerThanTabletSm();
  if (isTabletSm) {
    return <ContentHeaderMobile schoolType={schoolType} totalCount={<TotalLibraryCount className={'shrink-0 text-body-md-semibold text-neutral'} />} />;
  }
  return <div className={'flex h-[55px] items-center gap-lg self-stretch'} data-sentry-component="ContentHeader" data-sentry-source-file="ContentHeader.tsx">
      <div className={'flex h-[55px] w-full items-center gap-xs'}>
        <PublicDropdownSchool data-sentry-element="PublicDropdownSchool" data-sentry-source-file="ContentHeader.tsx" />
        <Title data-sentry-element="Title" data-sentry-source-file="ContentHeader.tsx" />
      </div>
    </div>;
};
const ContentHeaderMobile = (props: {
  totalCount: React.ReactNode;
  schoolType: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const [isShowInputSearch, setIsShown] = useState(false);
  const {
    searchText,
    handleSearchText,
    handleSearchType
  } = useSearchQuizQuery();
  const {
    clearSearchFilters
  } = usePublicSearchFilterQuery();
  const isSearchMode = Boolean(searchText);

  /**
   * 검색을 종료하고 초기 둘러보기 화면으로 돌아간다(PC SearchField.exitSearch 와 동일).
   * 검색어/필터/검색 타입을 비우면 둘러보기 헤더가 다시 노출되고,
   * 검색 열기 상태(isShowInputSearch)까지 닫아야 빈 입력창이 아닌 둘러보기 화면으로 복귀한다.
   */
  const exitSearch = () => {
    handleSearchText('');
    clearSearchFilters();
    handleSearchType(QuizsetFilterCriteriaSearchType.ALL);
    setIsShown(false);
  };
  return <div className={'flex w-full flex-col gap-xs'} data-sentry-component="ContentHeaderMobile" data-sentry-source-file="ContentHeader.tsx">
      {isSearchMode ? <div className={'flex flex-col gap-xs'}>
          <SearchInputQuizForm eventType={'public'} className={'h-[48px] w-full'} onBackClick={exitSearch} />
          <div className={'flex flex-col gap-3xs'}>
            <div className={'-mx-md flex items-center gap-3xs overflow-x-auto overflow-y-visible px-md'}>
              <SearchScopeDropdown className={'shrink-0'} />
              <SearchFilterBar className={'w-auto shrink-0 flex-nowrap'} />
            </div>
            <MobileNewMapBanner />
            <SearchMetaBarMobile eventType={'public'} totalCount={props.totalCount} />
          </div>
        </div> : <div className={'flex flex-col gap-xs'}>
          <div className={'flex w-full items-center justify-between gap-xs pb-3xs'}>
            {isShowInputSearch ? <SearchInputQuizForm eventType={'public'} autoFocus={true} className={'min-w-0 flex-1'} onBackClick={() => setIsShown(false)} /> : <>
                <div className="flex min-w-0 flex-1 items-center gap-xs">
                  <PublicDropdownSchool forceTitlePillOnMobile />
                  <Title className="shrink-0 whitespace-nowrap text-heading-lg-extrabold text-default" />
                </div>
                <Button onClick={() => setIsShown(true)} size={'md'} variant="transparent" intent={'neutral'} className="size-[40px] min-h-[40px] min-w-[40px] shrink-0 rounded-md p-none" aria-label={t('common:global.search')}>
                  <MagnifyingGlassIcon size={'md'} />
                </Button>
              </>}
          </div>
          <div className={'flex w-full items-center justify-between gap-xs'}>
            <DrawerFilter />
            <div className={'flex h-[40px] shrink-0 items-center gap-2xs'}>
              <div className={'flex min-w-0 items-center px-sm'}>
                {props.totalCount}
              </div>
              <div className={'h-[16px] w-[1px] border-l border-assistive'} />
              <SearchOrderDropDownMenu />
            </div>
          </div>
          <MobileNewMapBanner />
          <FeaturedQuizRollingBanner schoolType={props.schoolType} />
        </div>}
    </div>;
};
const MobileNewMapBanner = () => <div className="w-full pb-xs pt-md" data-sentry-component="MobileNewMapBanner" data-sentry-source-file="ContentHeader.tsx">
    <NewMapBanner size="sm" data-sentry-element="NewMapBanner" data-sentry-source-file="ContentHeader.tsx" />
  </div>;
const Title = ({
  className
}: {
  className?: string;
}) => {
  const {
    t,
    i18n
  } = useTranslation(['common']);
  return <span className={className ?? 'text-heading-xl-extrabold'} data-sentry-component="Title" data-sentry-source-file="ContentHeader.tsx">
      {formatEnglishTitleCase(t('common.quizExplore'), i18n.resolvedLanguage)}
    </span>;
};